// Modules
import React, { useState, useEffect, useRef }       from 'react'
import { Uploader }              from '@shared/uploader'
import TextArea                  from '@shared/textarea'
import { postRequest, pathname, csrfToken } from "@react/helpers/fetch"
import consumer from 'channels/consumer'

import { faRocketchat } from '@fortawesome/free-brands-svg-icons';

// Imported: on a page with no other React component, no icon is registered by name.
import { faPaperclip, faPaperPlane, faSpinner, faTimes, faBold, faItalic, faLink, faListOl, faListUl, faQuoteRight, faWandMagicSparkles, faUndo } from '@fortawesome/free-solid-svg-icons';
import { wrap, link, block } from '@react/helpers/markdown_format'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const CHANNEL      = "ChatGptChannel"
const initialState = { text: '', signedIds: [], files: [], loading: false}
const BOX_HEIGHT_KEY = 'ticketComposerHeight'
const BOX_MIN        = 60

// Only a ticket's messages are read as Markdown, so only its composer previews.
const MARKDOWN_RECORD = 'Ticket'

const Messages = ({ chatId, recordId, recordType, answerPath, polishPath, admin, userId, maxSizeMb, direct = false }) => {
  const uploaderRef = useRef()
  const composerRef = useRef()
  const mounted     = useRef(false)
  const [state, setState] = useState(initialState)
  const [preview, setPreview] = useState({ on: false, html: '', loading: false })
  const [selecting, setSelecting] = useState(false)
  const [polishing, setPolishing] = useState(false)
  const [beforePolish, setBeforePolish] = useState(null)
  const [answering, setAnswering] = useState(false)
  const answerSubscription = useRef(null)
  const [boxHeight, setBoxHeight] = useState(() => {
    try { return Number(window.localStorage.getItem(BOX_HEIGHT_KEY)) || null } catch (_e) { return null }
  })

  useEffect(() => {
    try {
      if (boxHeight) window.localStorage.setItem(BOX_HEIGHT_KEY, String(boxHeight))
      else window.localStorage.removeItem(BOX_HEIGHT_KEY)
    } catch (_e) { /* storage unavailable: the height lasts for the page */ }
  }, [boxHeight])

  // A drag that ends outside the box raises no select event on it: the page's
  // own selection and mouse-up events tell whether some of its text is selected.
  useEffect(() => {
    if (!direct || !admin) return

    const track = () => {
      const el = composerRef.current?.querySelector('textarea')
      if (!el || document.activeElement !== el) return
      setSelecting(el.selectionStart !== el.selectionEnd)
    }

    document.addEventListener('selectionchange', track)
    document.addEventListener('mouseup', track)
    return () => {
      document.removeEventListener('selectionchange', track)
      document.removeEventListener('mouseup', track)
    }
  }, [direct, admin])
  // Each preview request, or leaving the preview, bumps this, so a late answer
  // never reopens a preview that has since been closed.
  const previewRequest = useRef(0)

  // Leaving the preview, by hand or on send, puts the cursor back in the box.
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return }
    if (!preview.on) composerRef.current?.querySelector('textarea')?.focus()
  }, [preview.on])

  const markdown = recordType === MARKDOWN_RECORD

  const showPreview = () => {
    const request = ++previewRequest.current
    setPreview({ on: true, html: '', loading: true })
    postRequest({
      url:  pathname({ path: '/messages/preview' }),
      body: { chat_id: chatId, record_id: recordId, record_type: recordType, text: state.text }
    })
      .then(({ html }) => {
        if (request === previewRequest.current) setPreview({ on: true, html: html || '', loading: false })
      })
      .catch(() => {
        if (request === previewRequest.current) setPreview({ on: false, html: '', loading: false })
      })
  }

  const closePreview = () => {
    previewRequest.current++
    setPreview({ on: false, html: '', loading: false })
  }

  const tab = (label, active, onClick) => (
    <button type="button" className={`logs__tab ${active ? 'logs__tab--on' : ''}`} onClick={onClick}>
      {label}
    </button>
  )

  const onUpload = (signedIds, files) => {
    setState(prevState => ({
      ...prevState,
      signedIds: [...prevState.signedIds, ...signedIds],
      files:     [...prevState.files,     ...files]
    }))
  }

  const removeFile = index => {
    setState(prevState => {
      const newFiles     = [...prevState.files]
      const newSignedIds = [...prevState.signedIds]
      newFiles.splice(index, 1);
      newSignedIds.splice(index, 1);
      return { ...prevState, files: newFiles, signedIds: newSignedIds }
    })
  }

  const submit = () => {
    if (state.loading || polishing || answering) return
    if (!state.text && state.signedIds.length == 0) return

    setState(prevState => ({ ...prevState, loading: true }))
    const path   = "/messages"
    const format = "json"
    const url    = pathname({ path, format })
    const body   = {
      message:    { text: state.text },
      signed_ids: state.signedIds,
      chat_id:    chatId
    }

    postRequest({ url, body }).then(() => {
      setState(initialState)
      setSelecting(false)
      setBeforePolish(null)
      closePreview()
    })
  }

  useEffect(() => () => answerSubscription.current?.unsubscribe(), [])

  const answer = () => {
    const original = state.text
    if (answering || polishing || !original.trim()) return

    setAnswering(true)
    setSelecting(false)
    const url = pathname({ path: answerPath, format: "json" })
    const params = { channel: CHANNEL, user_id: userId }
    let asked = false
    // Asked once subscribed, else the reply can arrive first; reconnecting must not ask again.
    answerSubscription.current = consumer.subscriptions.create(params, {
      connected: () => {
        if (asked) return
        asked = true
        postRequest({ url, body: { text: original } })
      },
      received:  data => {
        answerSubscription.current?.unsubscribe()
        answerSubscription.current = null
        if (data.text?.trim()) {
          setState(prevState => ({ ...prevState, text: data.text.trim() }))
          setBeforePolish(original)
        }
        setAnswering(false)
      }
    })
  }

  // The AI support chat (Claude) can push one of its answers into this reply
  // box: it dispatches a `ticket:use-answer` event scoped to this record.
  useEffect(() => {
    const handler = event => {
      const detail = event.detail || {}
      if (detail.recordType !== recordType) return
      if (String(detail.recordId) !== String(recordId)) return
      if (!detail.text) return

      setState(prevState => ({ ...prevState, text: detail.text }))
    }

    window.addEventListener('ticket:use-answer', handler)

    // Going back remounts this box after the event fired, so a waiting answer
    // is claimed here instead.
    const pending = window.ticketPendingAnswer
    if (pending) {
      handler({ detail: pending })
      if (pending.recordType === recordType && String(pending.recordId) === String(recordId)) {
        window.ticketPendingAnswer = null
      }
    }

    return () => window.removeEventListener('ticket:use-answer', handler)
  }, [recordId, recordType])

  // Enter breaks the line; only the button sends.
  if (direct) {
    const textarea = () => composerRef.current?.querySelector('textarea')

    const format = transform => {
      const el = textarea()
      if (!el) return

      const result = transform(el.value, el.selectionStart, el.selectionEnd)
      setState(prevState => ({ ...prevState, text: result.text }))
      requestAnimationFrame(() => {
        const box = textarea()
        if (!box) return
        box.focus()
        box.setSelectionRange(result.start, result.end)
        setSelecting(result.start !== result.end)
      })
    }

    const FORMATS = [
      { key: 'bold',    icon: faBold,       shortcut: 'b', run: (t, s, e) => wrap(t, s, e, '**') },
      { key: 'italic',  icon: faItalic,     shortcut: 'i', run: (t, s, e) => wrap(t, s, e, '_') },
      { key: 'link',    icon: faLink,       shortcut: 'k', run: link },
      { key: 'ordered', icon: faListOl,     run: (t, s, e) => block(t, s, e, 'ordered') },
      { key: 'bullet',  icon: faListUl,     run: (t, s, e) => block(t, s, e, 'bullet') },
      { key: 'quote',   icon: faQuoteRight, run: (t, s, e) => block(t, s, e, 'quote') }
    ]

    const onKeyDown = e => {
      if (!admin || !(e.metaKey || e.ctrlKey)) return

      const match = FORMATS.find(f => f.shortcut && f.shortcut === e.key.toLowerCase())
      if (!match) return

      e.preventDefault()
      format(match.run)
    }

    const onSelect = e => setSelecting(e.target.selectionStart !== e.target.selectionEnd)

    const polish = async () => {
      const original = state.text
      if (polishing || answering || !original.trim()) return

      setPolishing(true)
      setSelecting(false)
      let polished = ''
      // A stream cut short (timeout, error, length) never sends `done`: its text stays a fragment.
      let finished = false

      try {
        const response = await fetch(polishPath, {
          method:      'POST',
          credentials: 'same-origin',
          headers:     { 'Content-Type': 'application/json', 'Accept': 'text/event-stream', 'X-CSRF-Token': csrfToken() },
          body:        JSON.stringify({ text: original })
        })
        if (!response.ok || !response.body) throw new Error(response.status)

        const reader  = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          const events = buffer.split('\n\n')
          buffer = events.pop()
          events.forEach(event => {
            const data = event.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trim()).join('')
            if (!data) return
            const payload = JSON.parse(data)
            if (payload.text) polished += payload.text
            if (payload.done) finished = true
          })

          const shown = polished
          setState(prevState => ({ ...prevState, text: shown }))
        }
      } catch (_error) {
        finished = false
      }

      if (finished && polished.trim()) {
        setState(prevState => ({ ...prevState, text: polished.trim() }))
        setBeforePolish(original)
      } else {
        setState(prevState => ({ ...prevState, text: original }))
      }
      setPolishing(false)
    }

    // Capped so the toolbar under the box always stays in the card.
    const startResize = event => {
      event.preventDefault()
      const box     = textarea()
      const card    = composerRef.current?.closest('#cards-container')
      const maxH    = Math.max(BOX_MIN, (card ? card.getBoundingClientRect().height : window.innerHeight) * 0.6)
      const startY  = event.clientY
      const startH  = box ? box.getBoundingClientRect().height : BOX_MIN

      const move = e => setBoxHeight(Math.round(Math.min(maxH, Math.max(BOX_MIN, startH + startY - e.clientY))))
      const stop = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', stop)
        document.body.classList.remove('select-none')
      }

      document.body.classList.add('select-none')
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', stop)
    }

    const undoPolish = () => {
      setState(prevState => ({ ...prevState, text: beforePolish }))
      setBeforePolish(null)
    }

    return (
      <div className={`slack-composer ${boxHeight ? 'slack-composer--sized' : ''}`} ref={composerRef}
           style={boxHeight ? { '--box-height': `${boxHeight}px` } : undefined}>
        { admin &&
          <div className="slack-composer__grip" onPointerDown={startResize} onDoubleClick={() => setBoxHeight(null)}
               title={I18n.t('ticket_assistant.composer_resize')}>
            <span />
          </div>
        }

        { admin && selecting &&
          <div className="slack-format-bar" onMouseDown={e => e.preventDefault()}>
            { FORMATS.map(f =>
              <button key={f.key} type="button" className="slack-format-bar__button"
                      title={I18n.t(`ticket_assistant.format.${f.key}`) + (f.shortcut ? ` (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl+'}${f.shortcut.toUpperCase()})` : '')}
                      onClick={() => format(f.run)}>
                <FontAwesomeIcon icon={f.icon} />
              </button>
            )}
          </div>
        }

        { state.files.length > 0 &&
          <div className="slack-composer__files">
            { state.files.map((file, index) =>
              <span key={index} className="slack-composer__file">
                <FontAwesomeIcon icon={faPaperclip} /> {file.name}
                <FontAwesomeIcon icon={faTimes} className="text-pointer" onClick={() => removeFile(index)} />
              </span>
            )}
          </div>
        }

        <TextArea
          className     = "slack-composer__input"
          placeholder   = {I18n.t('ticket_assistant.composer_placeholder')}
          onChange      = {text => setState((prevState) => ({ ...prevState, text }))}
          defaultValue  = {state.text}
          defaultHeight = {24}
          rows          = {1}
          onKeyDown     = {onKeyDown}
          readOnly      = {polishing || answering}
          onSelect      = {onSelect}
          onBlur        = {() => setSelecting(false)}
          onPaste       = {e => uploaderRef.current?.onPaste(e)}
          onDrop        = {e => uploaderRef.current?.onDrop(e)}
        />

        <div className="slack-composer__toolbar">
          <Uploader
            onUpload  = {onUpload}
            className = "slack-composer__icon"
            ref       = {uploaderRef}
            maxSizeMb = {maxSizeMb}
            multiple
          >
            <FontAwesomeIcon icon={faPaperclip} />
          </Uploader>

          { admin && answerPath &&
            <button type="button" className={`slack-composer__icon ${answering ? 'slack-composer__icon--busy' : ''}`}
                    onClick={answer} disabled={answering || polishing || !state.text.trim()}
                    title={I18n.t('ticket_assistant.composer_answer')}>
              <FontAwesomeIcon icon={answering ? faSpinner : faRocketchat} spin={answering} />
            </button>
          }

          { admin && polishPath &&
            <button type="button" className={`slack-composer__icon ${polishing ? 'slack-composer__icon--busy' : ''}`}
                    onClick={polish} disabled={polishing || answering || !state.text.trim()}
                    title={I18n.t('ticket_assistant.composer_polish')}>
              <FontAwesomeIcon icon={polishing ? faSpinner : faWandMagicSparkles} spin={polishing} />
            </button>
          }

          { beforePolish !== null && !polishing && !answering &&
            <button type="button" className="slack-composer__undo" onClick={undoPolish}>
              <FontAwesomeIcon icon={faUndo} /> {I18n.t('ticket_assistant.composer_undo_polish')}
            </button>
          }

          <button type="button" className="slack-composer__send" onClick={submit}
                  disabled={state.loading || polishing || answering || (!state.text && state.signedIds.length === 0)}>
            <FontAwesomeIcon icon={state.loading ? faSpinner : faPaperPlane} spin={state.loading} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      { markdown &&
        <div className="logs__tabs">
          { tab(I18n.t('messages.write'),   !preview.on, closePreview) }
          { tab(I18n.t('messages.preview'),  preview.on, showPreview) }
        </div>
      }

      { state.files.length > 0 &&
        <div className="text-right mb-2">
          { state.files.map((file, index) =>
              <strong key={index} className="m-0">
                {file.name}
                <FontAwesomeIcon icon={faTimes} className="text-pointer ml-2" onClick={() => removeFile(index)} />
                <br/>
              </strong>
          )}
        </div>
      }

      <div ref={composerRef} className="logs__chat d-grid" style={{ gridTemplateColumns: '1fr 50px 50px' }}>
        { preview.on
          ? <div className="logs__preview logs__message-body"
                 dangerouslySetInnerHTML = {{ __html: preview.loading ? '' : preview.html }} />
          : <TextArea
              onChange      = {text => setState((prevState) => ({ ...prevState, text }))}
              defaultValue  = {state.text}
              defaultHeight = {44}
              onPaste       = {e => uploaderRef.current?.onPaste(e)}
              onDrop        = {e => uploaderRef.current?.onDrop(e)}
            />
        }

        <Uploader
          onUpload  ={onUpload}
          className = "d-flex-center no-margins text-pointer a-s-stretch"
          ref       = {uploaderRef}
          maxSizeMb = {maxSizeMb}
          multiple
        >
          <FontAwesomeIcon icon={faPaperclip} size="lg"/>
        </Uploader>

        <div className="d-flex-center a-s-stretch text-pointer" onClick={submit}>
          <FontAwesomeIcon icon={state.loading ? faSpinner : faPaperPlane} size="lg" spin={state.loading} />
        </div>
      </div>
    </>
  )
}

export default Messages


